/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, react-hooks/exhaustive-deps, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import {
  Stack,
  Typography,
  Button,
  SelectChangeEvent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TextField,
  Autocomplete,
  InputAdornment,
  useMediaQuery,
  Popper,
  autocompleteClasses,
} from '@mui/material';
import { alpha, useTheme, styled, Theme } from '@mui/material/styles';
import {
  displayValue,
  formatAsCurrency,
  getEquitiesForScanner,
} from '../../../util';
import DeleteIcon from '@mui/icons-material/Delete';
import { useRecoilValue } from 'recoil';
import OptionsDropdownSelector from '../../shared/OptionsDropdownSelector';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  synthesizedEquitiesState,
  userIsLoggedInState,
  watchlistsState,
} from '../../../states';
import { useCompassCtx } from './useCompassCtx';
import {
  CompassChartData,
  CoreEquity,
  EquityFieldKey,
  ScannerID,
  StrategyCompassXYZAxis,
} from 'types';
import {
  SG_SPECIAL_SCANNERS,
  SG_STOCK_SCANNERS,
  WATCHLISTS_UPSELL,
} from 'config';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EditWatchlists } from 'components/watchlist/EditWatchlists';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import useToast from 'hooks/useToast';
import StockIcon from 'components/StockIcon';
import SearchIcon from '@mui/icons-material/Search';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { red } from '@mui/material/colors';
import { SGTooltip } from 'components/core';
import { UpsellModal } from 'components/shared/Upsell';
import { improvedFilterOptions } from 'util/shared/common';

const SG_SCANNER_OPTIONS = SG_SPECIAL_SCANNERS.map((scObj) => ({
  label: scObj.label.name,
  value: scObj.scanner,
  icon: scObj.label.icon,
}));
const STOCK_SCANNER_OPTIONS = SG_STOCK_SCANNERS.map((scObj) => ({
  label: scObj.label.name,
  value: scObj.scanner,
  icon: scObj.label.icon,
}));

const MAX_SYMBOLS = 50;
const LISTBOX_PADDING = 8; // px
const MAX_VISIBLE_SYMBOLS_IN_HISTORY_MODE = 20;

// Context for outer element props
const OuterElementContext = React.createContext({});

// Outer element type for the virtualized list
const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

// Hook to reset cache when data changes
function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Render row function for the virtualized list
function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  return (
    <Typography component="li" {...dataSet.props} noWrap style={inlineStyle}>
      {dataSet.option}
    </Typography>
  );
}

// Styled Popper component for Autocomplete
const StyledPopper = styled(Popper)(({ theme }) => ({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  [`& .${autocompleteClasses.option}`]: {
    padding: '8px 12px',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
  },
}));

// Virtualized listbox component for Autocomplete
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: Array<{ props: any; option: string }> = [];

  // Map children to flat array
  React.Children.forEach(children as React.ReactElement[], (child) => {
    if (child) {
      itemData.push({
        props: child.props,
        option: child.props.children,
      });
    }
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const itemCount = itemData.length;
  const itemSize = smUp ? 48 : 56; // Increased height for better visibility

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemCount * itemSize;
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={() => itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

// Sortable table row component using dnd-kit
const SortableTableRow = ({
  data,
  hiddenSymbols,
  toggleHiddenSymbols,
  deleteSym,
  setHoveredSymbol,
  xAxis,
  yAxis,
  zAxis,
  theme,
}: {
  data: CompassChartData;
  index: number;
  hiddenSymbols: Set<string>;
  toggleHiddenSymbols: (sym: string) => void;
  deleteSym: (sym: string) => void;
  setHoveredSymbol: (sym: string | undefined) => void;
  xAxis: string;
  yAxis: string;
  zAxis: string;
  theme: Theme;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: data.sym,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging
      ? alpha(theme.palette.primary.main, 0.3)
      : hiddenSymbols.has(data.sym)
        ? 'inherit'
        : alpha(theme.palette.primary.main, 0.1),
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.25),
      cursor: 'pointer',
    },
    '&:last-child td, &:last-child th': { border: 0 },
    '& td, & th': {
      borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
    },
  };

  return (
    <TableRow
      ref={setNodeRef}
      sx={style}
      onMouseEnter={() => setHoveredSymbol(data.sym)}
      onMouseLeave={() => setHoveredSymbol(undefined)}
      onClick={() => toggleHiddenSymbols(data.sym)}
    >
      <TableCell
        sx={{
          width: '30px',
          cursor: 'grab',
          '&:hover': {
            color: theme.palette.primary.main,
          },
        }}
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </TableCell>
      <TableCell
        component="th"
        scope="row"
        onClick={() => toggleHiddenSymbols(data.sym)}
        sx={{
          fontSize: { xs: 12, md: 14 },
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100px',
        }}
      >
        <Stack flex={1} direction="row" alignItems="center" spacing={2}>
          <StockIcon symbol={data.sym} />
          <Typography sx={{ fontSize: { xs: 12, md: 14 } }}>
            {data.sym}
            {data.symData.equity?.[EquityFieldKey.earningsDate] && (
              <SGTooltip title="Earnings within 3 weeks from now">
                <sup style={{ fontSize: 10, color: red[600] }}> E</sup>
              </SGTooltip>
            )}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="right">
        {formatAsCurrency(data.symData.price)}
      </TableCell>
      <TableCell align="right">
        {displayValue(data.unroundedX, xAxis as StrategyCompassXYZAxis)}
      </TableCell>
      <TableCell align="right">
        {displayValue(data.unroundedY, yAxis as StrategyCompassXYZAxis)}
      </TableCell>
      {data.unroundedZ != null && (
        <TableCell align="right">
          {displayValue(data.unroundedZ, zAxis as StrategyCompassXYZAxis)}
        </TableCell>
      )}
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toggleHiddenSymbols(data.sym);
          }}
          color="primary"
          sx={{ p: 0.5 }}
        >
          {hiddenSymbols.has(data.sym) ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          data-ph-capture-attribute-element="compass-symbol-delete"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            deleteSym(data.sym);
          }}
          color="primary"
          sx={{ p: 0.5 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

interface StrategyCompassEditSymbolsProps {
  compact?: boolean;
}

export const StrategyCompassEditSymbols = ({
  compact = false,
}: StrategyCompassEditSymbolsProps) => {
  const ctx = useCompassCtx();
  const watchlists = useRecoilValue(watchlistsState);
  const { openToast } = useToast();
  const isLoggedIn = useRecoilValue(userIsLoggedInState);
  const [upsellOpen, setUpsellOpen] = useState(false);

  const { chartData, visibleChartData } = ctx.compassData;
  const {
    setSyms,
    syms,
    zAxisDataKey,
    xAxis,
    yAxis,
    zAxis,
    setActiveWatchlistIdsState,
  } = ctx.compassParams;

  // Prefer the provider's equity map (always Total OI on /scanners) so
  // legacy-only scanners like Squeeze resolve regardless of the global model.
  const globalEquities = useRecoilValue(synthesizedEquitiesState);
  const equities = ctx.equityMap ?? globalEquities;
  const [showEditWatchlistModal, setShowEditWatchlistModal] = useState(false);
  const [maxSymbolsReached, setMaxSymbolsReached] = useState(false);
  const [symbolsWereTruncated, setSymbolsWereTruncated] = useState(false);
  const [sortedChartData, setSortedChartData] = useState<CompassChartData[]>(
    [],
  );
  const [orderedSyms, setOrderedSyms] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');

  const theme = useTheme();

  const [hiddenSymbols, setHiddenSymbols] = [
    ctx.hiddenSymbols,
    ctx.setHiddenSymbols,
  ] as const;
  const setHoveredSymbol = ctx.setHoveredSymbol;
  const compassHistoryLookback = ctx.config.historyLookback;

  useEffect(() => {
    if (
      compassHistoryLookback > 0 &&
      orderedSyms.length - hiddenSymbols.size >
        MAX_VISIBLE_SYMBOLS_IN_HISTORY_MODE
    ) {
      const numToHide =
        orderedSyms.length -
        hiddenSymbols.size -
        MAX_VISIBLE_SYMBOLS_IN_HISTORY_MODE;
      const symbolsToHide = orderedSyms
        .slice()
        .reverse()
        .filter((sym) => !hiddenSymbols.has(sym))
        .slice(0, numToHide);

      const newHiddenSyms = new Set([...hiddenSymbols, ...symbolsToHide]);

      setHiddenSymbols(newHiddenSyms);

      openToast({
        message: `Reduced visible symbols to ${MAX_VISIBLE_SYMBOLS_IN_HISTORY_MODE}
          symbols due to history mode. You may need to hide some other symbols
          if you're wanting to display a particular symbol.`,
        type: 'warning',
      });
    }
  }, [compassHistoryLookback, hiddenSymbols, setHiddenSymbols, orderedSyms]);

  // Use context for selected scanners and watchlists
  const selectedScanners = ctx.config.selectedScanners;
  const setSelectedScanners = (val: string[]) =>
    ctx.updateConfig({ selectedScanners: val });
  const selectedWatchlists = ctx.config.selectedWatchlists;
  const setSelectedWatchlists = (val: string[]) =>
    ctx.updateConfig({ selectedWatchlists: val });

  useEffect(() => {
    const seenCompass = localStorage.getItem('seenCompass');
    if (!isLoggedIn && selectedScanners.length === 0 && !seenCompass) {
      setSelectedScanners([ScannerID.VOL_RISK_PREMIUM]);
      localStorage.setItem('seenCompass', 'true');
    }
  }, [isLoggedIn, selectedScanners]);

  // Initialize sortedChartData and orderedSyms when chartData or syms changes
  useEffect(() => {
    // If we have an existing order, maintain it and add new items at the bottom
    if (orderedSyms.length > 0) {
      // Get symbols that are in chartData
      const chartSymbols = chartData.map((item) => item.sym);

      // Filter out ordered symbols that no longer exist in chartData
      const validOrderedSyms = orderedSyms.filter((sym) =>
        chartSymbols.includes(sym),
      );

      // Find new symbols that aren't in our ordered list yet
      const newSymbols = chartSymbols.filter(
        (sym) => !validOrderedSyms.includes(sym),
      );

      // Create the new complete ordered list with new symbols at the end
      const newOrderedSyms = [...validOrderedSyms, ...newSymbols];
      setOrderedSyms(newOrderedSyms);

      // Sort chartData according to our ordered list
      const newSortedData = [...chartData].sort((a, b) => {
        const indexA = newOrderedSyms.indexOf(a.sym);
        const indexB = newOrderedSyms.indexOf(b.sym);
        return indexA - indexB;
      });

      setSortedChartData(newSortedData);
    } else {
      // Initial setup - use the order from chartData
      setSortedChartData([...chartData]);
      setOrderedSyms(chartData.map((item) => item.sym));
    }
  }, [chartData, syms]);

  // Get all available symbols from manifest
  const allAvailableSymbols = useMemo(
    () => Array.from(equities.values()).map((eq) => eq.sym),
    [equities],
  );

  // Get equity details for autocomplete
  const equityDetails = useMemo(() => {
    const details = new Map<string, { name: string; sym: string }>();
    Array.from(equities.values()).forEach((eq) => {
      details.set(eq.sym, { name: eq.name || '', sym: eq.sym });
    });
    return details;
  }, [equities]);

  // Filter out any selected watchlists that no longer exist
  useEffect(() => {
    if (watchlists && selectedWatchlists.length > 0) {
      const existingWatchlistIds = new Set(watchlists.map((wl) => `${wl.id!}`));

      const validSelectedWatchlists = selectedWatchlists.filter((id) =>
        existingWatchlistIds.has(id),
      );

      // If some watchlists were removed, update the selected watchlists
      if (validSelectedWatchlists.length !== selectedWatchlists.length) {
        setSelectedWatchlists(validSelectedWatchlists);

        // Also update the active watchlist IDs in the compass params
        setActiveWatchlistIdsState(
          validSelectedWatchlists.map(Number).filter((id) => !isNaN(id)),
        );
      }
    }
  }, [
    watchlists,
    selectedWatchlists,
    setSelectedWatchlists,
    setActiveWatchlistIdsState,
  ]);

  const toggleHiddenSymbols = (sym: string) => {
    if (hiddenSymbols.has(sym)) {
      showSymbols([sym]);
    } else {
      hideSymbols([sym]);
    }
  };

  const hideSymbols = (syms: string[]) => {
    setHiddenSymbols(new Set([...hiddenSymbols].concat(syms)));
  };

  const showSymbols = (syms: string[]) => {
    const setArr = [...hiddenSymbols].filter((s) => !syms.includes(s));
    setHiddenSymbols(new Set(setArr));
  };

  const deleteSym = (sym: string) => {
    setSyms(syms.filter((s) => s !== sym));
    setHiddenSymbols(
      (hiddenSyms) => new Set([...hiddenSyms].filter((s) => s !== sym)),
    );
    // Also update our ordered list
    setOrderedSyms(orderedSyms.filter((s) => s !== sym));
    setMaxSymbolsReached(false);
    setSymbolsWereTruncated(false);
  };

  const deleteAllSyms = () => {
    setSyms([]);
    setHiddenSymbols(new Set());
    setActiveWatchlistIdsState([]);
    setOrderedSyms([]);
    setMaxSymbolsReached(false);
    setSymbolsWereTruncated(false);
    setSelectedScanners([]);
    setSelectedWatchlists([]);
  };

  const handleSymbolSearch = (
    _event: any,
    newValue: string | null | undefined,
  ) => {
    if (newValue == null) {
      return;
    }
    setSearchValue(newValue);

    // Check if symbol is already in the list
    if (syms.includes(newValue)) {
      openToast({
        message: `${newValue} is already in your symbols list`,
        type: 'error',
      });
      return;
    }

    // Check if we've reached the maximum number of symbols
    if (syms.length >= MAX_SYMBOLS) {
      setMaxSymbolsReached(true);
      openToast({
        message: `Maximum of ${MAX_SYMBOLS} symbols reached. Delete some symbols to add more.`,
        type: 'warning',
      });
      return;
    }

    // Add the new symbol at the beginning of the list
    setSyms([newValue, ...syms]);
    setSearchValue('');
  };

  useEffect(() => {
    const resultData = [...equities.values()];

    // Get symbols from currently selected scanners
    const selectedScannerSymbols = new Set(
      selectedScanners
        .filter(Boolean)
        .flatMap((id) => getEquitiesForScanner(id as ScannerID, resultData))
        .map((equity: CoreEquity) => equity.sym),
    );

    // Get symbols from selected watchlists to ensure we don't remove them
    // First, filter to make sure we only use watchlists that actually exist
    const existingWatchlistIds = new Set(
      watchlists?.map((wl) => `${wl.id!}`) || [],
    );

    const validSelectedWatchlists = selectedWatchlists.filter((id) =>
      existingWatchlistIds.has(id),
    );

    const selectedWatchlistSymbols = new Set(
      watchlists
        ?.filter((wl) => validSelectedWatchlists.includes(`${wl.id!}`))
        .flatMap((wl) => wl.symbols) ?? [],
    );
    const validWatchlistSymbols = [...selectedWatchlistSymbols].filter(
      (sym) => {
        const equity = equities.get(sym);
        return equity?.iv_rank != null && equity?.skew_rank != null;
      },
    );

    // Combine existing symbols with new scanner symbols
    // We keep:
    // 1. All symbols passing a chosen scanner
    // 2. All symbols from a chosen watchlist
    const uniqueSymbols = [
      ...new Set([...selectedScannerSymbols, ...validWatchlistSymbols]),
    ];
    setSymbolsWereTruncated(uniqueSymbols.length > MAX_SYMBOLS);
    setSyms(uniqueSymbols.slice(0, MAX_SYMBOLS));
  }, [selectedScanners, selectedWatchlists, equities]);

  const onScannerSelectChange = useCallback(
    (event: SelectChangeEvent<string | string[]>) => {
      setSelectedScanners(event.target.value as ScannerID[]);
    },
    [setSelectedScanners],
  );

  useEffect(() => {
    // Check if symbols exceed the maximum limit
    if (syms.length > MAX_SYMBOLS) {
      // Truncate symbols to the maximum allowed
      setSyms(syms.slice(0, MAX_SYMBOLS));
      setMaxSymbolsReached(true);
      setSymbolsWereTruncated(true);
    } else if (syms.length === MAX_SYMBOLS) {
      setMaxSymbolsReached(true);
      setSymbolsWereTruncated(false);
    } else {
      setMaxSymbolsReached(false);
      setSymbolsWereTruncated(false);
    }
  }, [syms, setSyms]);

  const watchlistOptions = useMemo(
    () =>
      watchlists?.map((w) => ({
        label: w.name,
        value: `${w.id!}`,
      })) ?? [],
    [watchlists],
  );

  const onEditWatchlists = () => {
    setShowEditWatchlistModal(true);
  };

  const onWatchlistSelectChange = useCallback(
    (event: SelectChangeEvent<string | string[]>) => {
      setSelectedWatchlists(event.target.value as string[]);
    },
    [],
  );

  // Set up sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = sortedChartData.findIndex(
      (item) => item.sym === active.id,
    );
    const newIndex = sortedChartData.findIndex((item) => item.sym === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      // Update the sorted chart data
      const newSortedData = arrayMove(sortedChartData, oldIndex, newIndex);
      setSortedChartData(newSortedData);

      // Update the ordered symbols list
      const newOrderedSyms = newSortedData.map((item) => item.sym);
      setOrderedSyms(newOrderedSyms);

      // Update the syms array to match the new order
      // Preserve any symbols that might not be in the chart data
      const chartSymbols = new Set(newOrderedSyms);
      const remainingSyms = syms.filter((sym) => !chartSymbols.has(sym));

      // Set the new symbols order
      setSyms([...newOrderedSyms, ...remainingSyms]);
    }
  };

  const onClickWatchlistsDropdown = () => {
    if (!isLoggedIn) {
      setUpsellOpen(true);
    }
  };

  return (
    <Stack
      sx={{
        gap: compact ? '6px' : '12px',
        padding: compact ? '6px' : '12px',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Autocomplete
        disableListWrap
        options={allAvailableSymbols}
        value={searchValue}
        inputValue={searchValue}
        open={searchValue.length > 0}
        onChange={handleSymbolSearch}
        onInputChange={(_, val) => {
          setSearchValue(val);
        }}
        filterOptions={improvedFilterOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              compact ? 'Search symbol...' : 'Search for a symbol...'
            }
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            onKeyDown={(evt) => {
              if (evt.key === 'Enter') {
                evt.preventDefault();
                const inputValue = (params.inputProps.value as string)
                  .trim()
                  .toUpperCase();
                handleSymbolSearch(evt, inputValue);
              }
            }}
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              width: '100%',
              '& fieldset': {
                borderColor: alpha(theme.palette.primary.main, 0.35),
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            }}
          />
        )}
        PopperComponent={StyledPopper}
        ListboxComponent={ListboxComponent}
        renderOption={(props, option) => {
          const equity = equityDetails.get(option);
          return (
            <li {...props} key={option} style={{ padding: '8px 12px' }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ width: '100%', gap: 2 }}
              >
                <StockIcon symbol={option} />
                <Stack sx={{ ml: '12px !important' }}>
                  <Typography fontWeight="bold">{option}</Typography>
                  {equity?.name && (
                    <Typography variant="caption" color="text.secondary">
                      {equity.name}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </li>
          );
        }}
        sx={{
          flexShrink: 0,
          mb: 2,
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.default,
          },
          '& .MuiAutocomplete-paper': {
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[3],
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        }}
      />

      <Stack
        sx={{
          flexDirection: compact ? 'column' : { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          gap: compact ? 0.5 : 4,
          width: '100%',
          flexShrink: 0,
        }}
      >
        <Stack
          sx={{
            flexDirection: 'row',
            gap: compact ? 0.5 : 4,
            flex: 1,
            width: '100%',
          }}
        >
          {isLoggedIn ? (
            <OptionsDropdownSelector
              label="Watchlists"
              value={selectedWatchlists}
              options={watchlistOptions}
              isMultiple
              onChange={onWatchlistSelectChange}
              onEdit={onEditWatchlists}
              isEditable
              editLabel="Edit Watchlists"
              sx={{
                minWidth: { xs: '100%', sm: '190px' },
                flex: { xs: 'none', sm: 1 },
              }}
            />
          ) : (
            <Button
              variant="outlined"
              onClick={onClickWatchlistsDropdown}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                minWidth: { xs: '100%', sm: '190px' },
                flex: { xs: 'none', sm: 1 },
                height: 32,
                fontSize: 12,
                textTransform: 'none',
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                textAlign: 'left',
                justifyContent: 'space-between',
                paddingLeft: '12px',
                paddingRight: '8px',
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
                '& .MuiButton-endIcon': {
                  marginLeft: 'auto',
                  color: theme.palette.primary.main,
                },
              }}
            >
              Watchlists
            </Button>
          )}
          <OptionsDropdownSelector
            label="Scanners"
            value={selectedScanners}
            sections={[
              {
                title: 'SG Scanners',
                options: SG_SCANNER_OPTIONS,
              },
              {
                title: 'Stock Scanners',
                options: STOCK_SCANNER_OPTIONS,
              },
            ]}
            isMultiple
            onChange={onScannerSelectChange}
            maxHeight="400px"
          />
        </Stack>
        <Stack
          sx={{
            flexDirection: 'row',
            gap: compact ? 0.5 : 2,
            justifyContent: compact
              ? 'flex-end'
              : { xs: 'space-between', md: 'flex-start' },
            flexShrink: 0,
          }}
        >
          <Button
            size={compact ? 'small' : 'medium'}
            sx={{
              textTransform: 'none',
              fontSize: compact ? '11px' : '12px',
              minWidth: compact ? 'auto' : '75px',
              px: compact ? 1 : undefined,
              flex: compact ? 'none' : { xs: 1, md: 'none' },
            }}
            onClick={() =>
              visibleChartData.length === 0
                ? showSymbols(syms)
                : hideSymbols(syms)
            }
            disabled={chartData.length === 0}
          >
            {visibleChartData.length === 0 ? 'Show All' : 'Hide All'}
          </Button>
          <Button
            size={compact ? 'small' : 'medium'}
            sx={{
              textTransform: 'none',
              fontSize: compact ? '11px' : '12px',
              minWidth: compact ? 'auto' : '75px',
              px: compact ? 1 : undefined,
              flex: compact ? 'none' : { xs: 1, md: 'none' },
            }}
            onClick={deleteAllSyms}
          >
            Clear All
          </Button>
        </Stack>
      </Stack>

      {maxSymbolsReached && (
        <Alert
          severity="warning"
          sx={{
            my: 1,
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
            color: 'text.primary',
          }}
        >
          {symbolsWereTruncated
            ? `Maximum of ${MAX_SYMBOLS} symbols reached. Some symbols were removed to stay within the limit.`
            : `Maximum of ${MAX_SYMBOLS} symbols reached. Delete some symbols to add more.`}
        </Alert>
      )}

      {chartData.length === 0 ? (
        <Stack
          sx={{
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            height: '100%',
          }}
        >
          <Stack
            sx={{
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              color: 'text.secondary',
            }}
          >
            <Typography variant="h6">No Equities Found</Typography>
            <Typography variant="body2">
              Select a watchlist or scanner to view equities
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            ...(compact
              ? { flex: 1, minHeight: 0 }
              : {
                  maxHeight: {
                    xs: '550px',
                    lg: '470px',
                  },
                }),
            overflowY: 'auto',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '30px' }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Symbol</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    Price
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    X
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    Y
                  </TableCell>
                  {zAxisDataKey != null && (
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      Z
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <SortableContext
                  items={sortedChartData.map((data) => data.sym)}
                  strategy={verticalListSortingStrategy}
                >
                  {sortedChartData.map((data, index) => (
                    <SortableTableRow
                      key={data.sym}
                      data={data}
                      index={index}
                      hiddenSymbols={hiddenSymbols}
                      toggleHiddenSymbols={toggleHiddenSymbols}
                      deleteSym={deleteSym}
                      setHoveredSymbol={setHoveredSymbol}
                      xAxis={xAxis}
                      yAxis={yAxis}
                      zAxis={zAxis || ''}
                      theme={theme}
                    />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        </TableContainer>
      )}

      <EditWatchlists
        onClose={() => setShowEditWatchlistModal(false)}
        open={showEditWatchlistModal}
        modal={true}
        // toast needs to be displayed after the modal disappears
        openToast={(toastData) => setTimeout(() => openToast(toastData), 1_000)}
      />

      <UpsellModal
        open={upsellOpen}
        setOpen={setUpsellOpen}
        title={WATCHLISTS_UPSELL.title}
        subtitle={WATCHLISTS_UPSELL.subtitle}
        items={WATCHLISTS_UPSELL.items}
      />
    </Stack>
  );
};
